import { swapDatabase } from './swapDatabase';
import { getTokenAddress, getTokensForNetwork, getTokenConfig } from '../config/tokens';
import { getNetworkDisplayName } from '../config/networks';
import { fetchTokenPrices } from './binancePriceService';
import { bscScanService, FormattedTransfer } from './bscScanService';
import { csvDataLoader } from './csvDataLoader';
import { solscanService, FormattedSolTransfer } from './solscanService';

export interface TransactionGeneratorConfig {
  interval: number; // milliseconds
  autoStart: boolean;
  networkId: number;
}

export interface RecentTransaction {
  type: 'Swap' | 'Transfer';
  from: string;
  to: string;
  fromAmount: string;
  toAmount: string;
  amount: string;
  time: string;
  status: 'pending' | 'confirmed' | 'failed';
  fromLogo: string;
  toLogo: string;
  txHash: string;
  timestamp: number;
  transactionType?: 'swap' | 'transfer';
}

class TransactionService {
  private static instance: TransactionService;
  private generateInterval: NodeJS.Timeout | null = null;
  private subscribers: Set<(transaction: RecentTransaction) => void> = new Set();
  private isGenerating = false;
  private config: TransactionGeneratorConfig = {
    interval: 30000, // Base interval (actual generation is random 30s-3m)
    autoStart: true, // FIXED: Enabled auto-start to populate transactions
    networkId: 1 // Default to Ethereum
  };

  static getInstance(): TransactionService {
    if (!this.instance) {
      this.instance = new TransactionService();
    }
    return this.instance;
  }

  private constructor() {
    // FIXED: Auto-start enabled to populate transactions
    // Transaction generation starts automatically
    console.log('🔧 TransactionService initialized - auto-start enabled for transaction population');
    if (this.config.autoStart) {
      this.startGeneration();
    }
  }

  // Subscribe to new transaction notifications
  subscribe(callback: (transaction: RecentTransaction) => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  // Notify all subscribers of new transaction
  private notifySubscribers(transaction: RecentTransaction): void {
    this.subscribers.forEach(callback => {
      try {
        callback(transaction);
      } catch (error) {
        console.error('Error in transaction subscriber:', error);
      }
    });
  }

  // Update configuration
  updateConfig(config: Partial<TransactionGeneratorConfig>): void {
    this.config = { ...this.config, ...config };
    
    // Restart generation if interval changed
    if (this.isGenerating) {
      this.stopGeneration();
      this.startGeneration();
    }
  }

  // Start transaction generation
  startGeneration(): void {
    if (this.isGenerating) {
      console.log('Transaction generation already running');
      return;
    }

    console.log(`🔄 Starting transaction generation with random intervals (30s - 3m)`);
    this.isGenerating = true;
    
    // Generate first transaction immediately
    this.generateTransaction();
    
    // Schedule next transaction
    this.scheduleNextTransaction();
  }

  // Schedule next transaction with random delay
  private scheduleNextTransaction(): void {
    if (!this.isGenerating) return;

    // Random time between 30 seconds (30000ms) and 3 minutes (180000ms)
    const minDelay = 30000;
    const maxDelay = 180000;
    const delay = Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;

    console.log(`⏳ Next transaction scheduled in ${Math.floor(delay / 1000)} seconds`);

    this.generateInterval = setTimeout(() => {
      this.generateTransaction();
      this.scheduleNextTransaction();
    }, delay);
  }

  // Stop transaction generation
  stopGeneration(): void {
    if (this.generateInterval) {
      clearTimeout(this.generateInterval);
      this.generateInterval = null;
    }
    this.isGenerating = false;
    console.log('⏹️ Transaction generation stopped');
  }

  // Restart transaction generation
  restartGeneration(): void {
    console.log('🔄 Restarting transaction generation...');
    this.stopGeneration();
    setTimeout(() => {
      this.startGeneration();
    }, 1000); // Small delay before restart
  }

  // Generate a new realistic transaction
  private async generateTransaction(): Promise<void> {
    try {
      const networkTokens = getTokensForNetwork(this.config.networkId);
      if (networkTokens.length < 2) {
        console.warn('Not enough tokens available for transaction generation');
        return;
      }

      // Realistic token pairs with higher probability for stablecoins
      const tokens = ['USDP', 'ETH', 'USDC', 'USDT', 'WBTC', 'BNB', 'LINK', 'UNI', 'AAVE'];
      const availableTokens = tokens.filter(token => 
        networkTokens.find(nt => nt.symbol === token)
      );

      if (availableTokens.length < 2) {
        console.warn('Not enough matching tokens for generation');
        return;
      }

      const fromToken = availableTokens[Math.floor(Math.random() * availableTokens.length)];
      let toToken = availableTokens[Math.floor(Math.random() * availableTokens.length)];
      
      // Ensure different tokens
      while (toToken === fromToken && availableTokens.length > 1) {
        toToken = availableTokens[Math.floor(Math.random() * availableTokens.length)];
      }

      // Get token configs for price and logo information
      const fromTokenConfig = networkTokens.find(t => t.symbol === fromToken);
      const toTokenConfig = networkTokens.find(t => t.symbol === toToken);

      if (!fromTokenConfig || !toTokenConfig) {
        console.warn('Token configuration not found');
        return;
      }

      // Generate realistic amounts based on token type and real prices
      const { fromAmount, toAmount } = await this.generateRealisticAmounts(fromToken, toToken);
      
      // Generate transaction hash and other details
      const txHash = this.generateTxHash();
      const userAddress = this.generateRandomAddress();
      const timestamp = Date.now();

      // Create the swap transaction for database
      const swapTransaction = await swapDatabase.saveSwap({
        fromToken,
        toToken,
        fromAmount: fromAmount.toString(),
        toAmount: toAmount.toString(),
        fromTokenLogo: fromTokenConfig.logoUrl || 'USDP.png',
        toTokenLogo: toTokenConfig.logoUrl || 'USDP.png',
        userAddress,
        txHash,
        blockNumber: Math.floor(Math.random() * 1000000) + 15000000,
        gasUsed: (21000 + Math.floor(Math.random() * 50000)).toString(),
        gasPrice: (20 + Math.random() * 30).toFixed(9),
        status: Math.random() > 0.05 ? 'confirmed' : (Math.random() > 0.5 ? 'pending' : 'failed'), // 95% confirmed, 2.5% pending, 2.5% failed
        chainId: this.config.networkId,
        networkName: getNetworkDisplayName(this.config.networkId),
        priceImpact: Math.random() * 2, // 0-2% price impact
        slippage: 0.5,
        route: [fromToken, toToken],
        fromTokenAddress: getTokenAddress(fromToken, this.config.networkId),
        toTokenAddress: getTokenAddress(toToken, this.config.networkId),
        type: 'swap'
      });

      // Create recent transaction format for UI components
      const recentTransaction: RecentTransaction = {
        type: 'Swap',
        from: fromToken,
        to: toToken,
        fromAmount: fromAmount.toString(),
        toAmount: toAmount.toString(),
        amount: `${fromAmount} ${fromToken} → ${toAmount} ${toToken}`,
        time: this.formatTimeAgo(timestamp),
        status: swapTransaction.status,
        fromLogo: fromTokenConfig.logoUrl || 'USDP.png',
        toLogo: toTokenConfig.logoUrl || 'USDP.png',
        txHash,
        timestamp,
        transactionType: 'swap'
      };

      console.log(`✅ Generated transaction: ${recentTransaction.amount}`);
      
      // Notify all subscribers
      this.notifySubscribers(recentTransaction);

    } catch (error) {
      console.error('Failed to generate transaction:', error);
    }
  }

  // Generate realistic amounts based on token types using real market prices when available
  private async generateRealisticAmounts(fromToken: string, toToken: string): Promise<{ fromAmount: number; toAmount: number }> {
    let fromAmount: number;

    // Generate amounts based on token type
    if (fromToken === 'USDP' || fromToken === 'USDC' || fromToken === 'USDT') {
      // Stablecoins: $100 - $50,000
      fromAmount = Math.floor(Math.random() * 49900) + 100;
    } else if (fromToken === 'ETH') {
      // ETH: 0.1 - 50 ETH
      fromAmount = parseFloat((Math.random() * 49.9 + 0.1).toFixed(4));
    } else if (fromToken === 'WBTC') {
      // Bitcoin: 0.01 - 5 BTC
      fromAmount = parseFloat((Math.random() * 4.99 + 0.01).toFixed(4));
    } else if (fromToken === 'BNB') {
      // BNB: 1 - 100 BNB
      fromAmount = parseFloat((Math.random() * 99 + 1).toFixed(2));
    } else {
      // Other tokens: 10 - 10,000
      fromAmount = Math.floor(Math.random() * 9990) + 10;
    }

    // Resolve CoinGecko IDs
    const COINGECKO_ID_OVERRIDES: Record<string, string> = {
      USDP: 'paxos-standard',
      USDC: 'usd-coin',
      USDT: 'tether',
      ETH: 'ethereum',
      WBTC: 'wrapped-bitcoin',
      BNB: 'binancecoin',
      LINK: 'chainlink',
      UNI: 'uniswap',
      AAVE: 'aave'
    };
    const resolveId = (symbol: string) => {
      const cfg = getTokenConfig(symbol);
      return COINGECKO_ID_OVERRIDES[symbol as keyof typeof COINGECKO_ID_OVERRIDES] || cfg?.coingeckoId || symbol.toLowerCase();
    };

    const fromId = resolveId(fromToken);
    const toId = resolveId(toToken);

    // Default fallback prices
    const fallbackPrices: Record<string, number> = {
      USDP: 1.0,
      USDC: 1.0,
      USDT: 1.0,
      ETH: 3250.0,
      WBTC: 101000.0,
      BNB: 650.0,
      LINK: 14.7,
      UNI: 12.5,
      AAVE: 180.0
    };

    let fromPrice = fallbackPrices[fromToken] ?? 1.0;
    let toPrice = fallbackPrices[toToken] ?? 1.0;

    try {
      const priceData = await fetchTokenPrices([fromId, toId]);
      if (priceData && priceData[fromId]?.usd) {
        fromPrice = priceData[fromId].usd;
      }
      if (priceData && priceData[toId]?.usd) {
        toPrice = priceData[toId].usd;
      }
    } catch (e) {
      console.warn('Failed to fetch real prices, using fallback prices', e);
    }

    const fromValue = fromAmount * fromPrice;
    const toAmount = parseFloat((fromValue / toPrice).toFixed(6));

    return { fromAmount, toAmount };
  }

  // Generate random transaction hash
  private generateTxHash(): string {
    const chars = '0123456789abcdef';
    let result = '0x';
    for (let i = 0; i < 64; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  }

  // Generate random wallet address
  private generateRandomAddress(): string {
    return '0x' + Math.random().toString(16).substr(2, 40);
  }

  // Format time ago string
  private formatTimeAgo(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  }

  // Convert BSCScan transfer to SwapTransaction format
  private convertTransferToSwapTransaction(transfer: FormattedTransfer): RecentTransaction {
    const tokenConfig = getTokenConfig(transfer.tokenSymbol);
    
    return {
      type: 'Transfer',
      from: transfer.from, // Wallet address (from)
      to: transfer.to, // Wallet address (to)
      fromAmount: transfer.amount,
      toAmount: transfer.amount,
      amount: `${transfer.amount} ${transfer.tokenSymbol}`,
      time: this.formatTimeAgo(transfer.timestamp),
      status: transfer.status,
      fromLogo: tokenConfig?.logoUrl || '/USDPlogo.png',
      toLogo: tokenConfig?.logoUrl || '/USDPlogo.png',
      txHash: transfer.txHash,
      timestamp: transfer.timestamp,
      transactionType: 'transfer'
    };
  }

  // Convert Solscan transfer to SwapTransaction format
  private convertSolTransferToSwapTransaction(transfer: FormattedSolTransfer): RecentTransaction {
    const tokenConfig = getTokenConfig('USDP');
    
    return {
      type: 'Transfer',
      from: transfer.from, // Wallet address (from)
      to: transfer.to, // Wallet address (to)
      fromAmount: transfer.amount,
      toAmount: transfer.amount,
      amount: `${transfer.amount} ${transfer.tokenSymbol}`,
      time: this.formatTimeAgo(transfer.timestamp),
      status: transfer.status,
      fromLogo: tokenConfig?.logoUrl || '/USDPlogo.png',
      toLogo: tokenConfig?.logoUrl || '/USDPlogo.png',
      txHash: transfer.txHash,
      timestamp: transfer.timestamp,
      transactionType: 'transfer'
    };
  }

  // Get USD.P token transfers from CSV, BSCScan, and Solscan (combine all sources)
  async getUSDPTransfers(limit: number = 50): Promise<RecentTransaction[]> {
    try {
      const allTransfers: FormattedTransfer[] = [];
      const allSolTransfers: FormattedSolTransfer[] = [];
      
      // Load from CSV file
      console.log('📂 Loading USD.P transfers from CSV data...');
      try {
        const csvTransfers = await csvDataLoader.getTransfers(1, limit);
        if (csvTransfers.length > 0) {
          console.log(`✅ Loaded ${csvTransfers.length} transfers from CSV`);
          allTransfers.push(...csvTransfers);
        }
      } catch (csvError) {
        console.warn('⚠️ CSV loading failed:', csvError);
      }

      // Also fetch from BSCScan API for real blockchain data
      console.log('🌐 Fetching USD.P transfers from BSCScan API...');
      try {
        const bscTransfers = await bscScanService.getUSDPTransfers(1, limit);
        if (bscTransfers.length > 0) {
          console.log(`✅ Loaded ${bscTransfers.length} transfers from BSCScan`);
          allTransfers.push(...bscTransfers);
        } else {
          const lastError = bscScanService.getLastError();
          if (lastError && lastError.includes('API')) {
            console.warn('⚠️ BSCScan API error:', lastError);
          }
        }
      } catch (bscError) {
        console.warn('⚠️ BSCScan API failed:', bscError);
      }

      // Fetch from Solscan API for Solana blockchain data
      console.log('🌐 Fetching USD.P transfers from Solscan API...');
      try {
        const solTransfers = await solscanService.getUSDPTransfers(1, limit);
        if (solTransfers.length > 0) {
          console.log(`✅ Loaded ${solTransfers.length} transfers from Solscan`);
          allSolTransfers.push(...solTransfers);
        } else {
          const lastError = solscanService.getLastError();
          if (lastError && lastError.includes('API')) {
            console.warn('⚠️ Solscan API error:', lastError);
          }
        }
      } catch (solError) {
        console.warn('⚠️ Solscan API failed:', solError);
      }
      
      // If all sources failed, use demo data
      if (allTransfers.length === 0 && allSolTransfers.length === 0) {
        console.warn('⚠️ No data from CSV, BSCScan, or Solscan, using demo data');
        const bscDemoTransfers = bscScanService.generateDemoTransfers(Math.min(limit / 2, 10));
        const solDemoTransfers = solscanService.generateDemoTransfers(Math.min(limit / 2, 10));
        allTransfers.push(...bscDemoTransfers);
        allSolTransfers.push(...solDemoTransfers);
      }
      
      // Remove duplicates based on transaction hash and sort by timestamp
      const uniqueBscTransfers = Array.from(
        new Map(allTransfers.map(t => [t.txHash, t])).values()
      );
      
      const uniqueSolTransfers = Array.from(
        new Map(allSolTransfers.map(t => [t.txHash, t])).values()
      );
      
      // Convert both to RecentTransaction format and combine
      const bscTransactions = uniqueBscTransfers.map(transfer => this.convertTransferToSwapTransaction(transfer));
      const solTransactions = uniqueSolTransfers.map(transfer => this.convertSolTransferToSwapTransaction(transfer));
      
      const combined = [...bscTransactions, ...solTransactions].sort((a, b) => b.timestamp - a.timestamp);
      
      console.log(`📊 Total unique transfers: ${combined.length} (CSV + BSCScan + Solscan + Demo)`);
      
      // Limit results
      return combined.slice(0, limit);
    } catch (error) {
      console.error('Failed to fetch USDP transfers:', error);
      return [];
    }
  }

  // Get recent transactions from database
  async getRecentTransactions(limit: number = 10): Promise<RecentTransaction[]> {
    try {
      const swaps = await swapDatabase.getSwaps(limit, 0);
      return swaps.map(swap => ({
        type: 'Swap',
        from: swap.fromToken,
        to: swap.toToken,
        fromAmount: swap.fromAmount,
        toAmount: swap.toAmount,
        amount: `${swap.fromAmount} ${swap.fromToken} → ${swap.toAmount} ${swap.toToken}`,
        time: this.formatTimeAgo(swap.timestamp),
        status: swap.status,
        fromLogo: swap.fromTokenLogo,
        toLogo: swap.toTokenLogo,
        txHash: swap.txHash,
        timestamp: swap.timestamp,
        transactionType: swap.type || 'swap'
      }));
    } catch (error) {
      console.error('Failed to get recent transactions:', error);
      return [];
    }
  }

  // Get combined transactions (swaps + transfers)
  async getCombinedTransactions(limit: number = 50): Promise<RecentTransaction[]> {
    try {
      // Get both swap transactions and USDP transfers
      const [swaps, transfers] = await Promise.all([
        this.getRecentTransactions(limit),
        this.getUSDPTransfers(limit)
      ]);

      // Combine and sort by timestamp
      const combined = [...swaps, ...transfers].sort((a, b) => b.timestamp - a.timestamp);

      // Return limited results
      return combined.slice(0, limit);
    } catch (error) {
      console.error('Failed to get combined transactions:', error);
      return [];
    }
  }

  // Get status information
  getStatus(): { isGenerating: boolean; config: TransactionGeneratorConfig; subscriberCount: number } {
    return {
      isGenerating: this.isGenerating,
      config: { ...this.config },
      subscriberCount: this.subscribers.size
    };
  }
}

// Export singleton instance
export const transactionService = TransactionService.getInstance();